import { authFetch } from 'api/auth/authFetch'
import { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch  } from 'react-redux'

/**
 * Fetches works for a specific contact with pagination and sorting
 * @param {string} contactId - The contact GUID
 * @param {number} count - Number of items per page
 * @param {number} page - Page number (1-based)
 * @param {string} filter - Optional filter criteria
 * @param {string} sort - Optional sort expression
 * @param {string} endpoint - The base API endpoint URL
 * @param {AbortController} controller - Optional abort controller for cancellation
 * @returns {Promise<Object>} Object with total count and results array
 */
export const fetchContactWorks = async (contactId, count, page, filter, sort, endpoint, controller) => {
    if (!contactId || !endpoint) {
        throw new Error('ContactId and endpoint are required')
    }

    // Build query parameters
    const queryParams = new URLSearchParams({
        ...(count && { count: count.toString() }),
        ...(page && { pageNumber: page.toString() }),
        ...(filter && { filter: filter.toString() }),
        ...(sort && { sortby: sort.toString() })
    }).toString()

    const url = `${endpoint}/Contact/${contactId}/Works${queryParams ? `?${queryParams}` : ''}`
    let responseCode = -1

    try {
        const response = await authFetch(url, {
            method: 'GET',
            headers: { "Content-Type": 'application/json' },
            signal: controller?.signal
        })

        if (controller?.signal?.aborted) return null

        responseCode = response.status
        if (responseCode === 200) {
            const data = await response.json()
            // Return full response with total and results
            return data
        }

        throw {
            Message: 'The Contact Works fetch request was not successful',
            StatusCode: responseCode,
            Details: await response.text()
        }
    } catch (error) {
        if (controller?.signal?.aborted) return null
        
        throw {
            Message: 'The Contact Works fetch request failed',
            StatusCode: responseCode,
            Details: error
        }
    }
}

/**
 * Fetches works for a specific contact (simple version without pagination)
 * @param {string} contactId - The contact GUID
 * @param {string} endpoint - The base API endpoint URL
 * @param {AbortController} controller - Optional abort controller for cancellation
 * @returns {Promise<Array>} Array of contact works
 */
export const fetchContactWorksSimple = async (contactId, endpoint, controller) => {
    const data = await fetchContactWorks(contactId, null, null, null, null, endpoint, controller)
    return data?.results || []
}

/**
 * Creates an abort controller and fetch function for contact works (simple version)
 * @param {string} contactId - The contact GUID
 * @param {string} endpoint - The base API endpoint URL
 * @returns {Object} Object containing controller and fetch function
 */
export const setupContactWorksFetch = (contactId, endpoint) => {
    const controller = new AbortController()
    return {
        controller,
        fetch: () => fetchContactWorksSimple(contactId, endpoint, controller)
    }
}

/**
 * Creates an abort controller and fetch function for contact works with pagination
 * @param {string} contactId - The contact GUID
 * @param {number} count - Number of items per page
 * @param {number} page - Page number (1-based)
 * @param {string} filter - Optional filter criteria
 * @param {string} sort - Optional sort expression
 * @param {string} endpoint - The base API endpoint URL
 * @returns {Object} Object containing controller and fetch function
 */
export const setupContactWorksFetchWithPaging = (contactId, count, page, filter, sort, endpoint) => {
    const controller = new AbortController()
    return {
        controller,
        fetch: () => fetchContactWorks(contactId, count, page, filter, sort, endpoint, controller)
    }
}

// Helper function to fetch works data
const fetchWorksData = async (contactId, count, page, filter, sort, endpoint, controller) => {
    if (!contactId || count === 0 || page === 0) return { total: 0, results: [] }

    try {
        const data = await fetchContactWorks(contactId, count, page, filter, sort, endpoint, controller)
        
        if (controller?.signal?.aborted) return null
        
        return data || { total: 0, results: [] }
    } catch (error) {
        if (controller?.signal?.aborted) return null
        
        throw {
            Message: 'The Contact Works fetch request was not successful',
            StatusCode: error.StatusCode || -1,
            Details: error
        }
    }
}

// Setup fetch function
const setUpFetch = (contactId, count, page, filter, sort, url) => {
    const controller = new AbortController()
    // Extract base URL from the full URL (remove query params if any)
    const endpoint = url ? url.split('?')[0].replace(`/Contact/${contactId}/Works`, '') : null
    
    return {
        controller,
        fetch: () => fetchWorksData(contactId, count, page, filter, sort, endpoint, controller)
    }
}

// React component for Contact Works API
const ContactWorksApi = (props) => {
    const { contactId, endpoint, setStatusAction, setResultsAction, requestSelector, setRefreshAction } = props

    /* Dispatch */
    const dispatch = useDispatch()
    
    /* Selectors */
    const apiRequest = useSelector(requestSelector)
    const { currentContactId, count, page, filter, sort, status } = apiRequest

    // Track the last fetched parameters to avoid duplicate fetches
    const lastFetchedRef = useRef(null)

    // Use useEffect to handle data fetching
    useEffect(() => {
        // Don't fetch if contactId doesn't match currentContactId (wrong contact data)
        if (currentContactId !== contactId) {
            return
        }

        // Don't fetch if already pending (avoid duplicate requests)
        if (status?.status === 'pending') {
            return
        }

        // Create a key representing current fetch parameters
        const currentParamsKey = JSON.stringify({ currentContactId, count, page, filter, sort })

        // Don't fetch if we already fetched with these exact parameters
        if (lastFetchedRef.current === currentParamsKey && status?.status === 'complete') {
            return
        }

        // Remember these parameters
        lastFetchedRef.current = currentParamsKey

        // Set pending status
        dispatch(setStatusAction(contactId, { status: 'pending' }))

        const controller = new AbortController()

        fetchContactWorks(currentContactId, count, page, filter, sort, endpoint, controller)
            .then(results => {
                if (results && !controller.signal.aborted) {
                    dispatch(setResultsAction(contactId, results))
                    dispatch(setStatusAction(contactId, { status: 'complete' }))
                }
            })
            .catch(error => {
                if (!controller.signal.aborted) {
                    console.error('ContactWorksApi: Fetch error', error)
                    dispatch(setResultsAction(contactId, { total: 0, results: [] }))
                    dispatch(setStatusAction(contactId, { status: 'error', error }))
                    // Clear the last fetched key on error so it can retry
                    lastFetchedRef.current = null
                }
            })

        // Cleanup function to abort request if component unmounts or dependencies change
        return () => {
            controller.abort()
        }
    }, [contactId, currentContactId, count, page, filter, sort, endpoint, dispatch, setStatusAction, setResultsAction])

    // This component doesn't render anything
    return null
}

export default ContactWorksApi


